import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/enviroment.prod';

/**
 * Attaches the Sanctum bearer token to API calls.
 *
 * The token has always been stored but never sent — only the project/cart
 * routes are behind auth:api, so this is inert for every pre-existing
 * endpoint. It reads localStorage directly rather than injecting AuthService,
 * because AuthService injects HttpClient and that would be a DI cycle.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly USER_KEY = 'pca_user';

  /**
   * Only the project/cart endpoints are authenticated. Scoping the header to
   * them keeps every other call preflight-free — adding an Authorization
   * header turns a simple cross-origin GET into an OPTIONS + GET pair, and
   * there is no reason to pay that on endpoints that ignore the token.
   *
   * Extend this list when more routes move behind auth:api.
   */
  private static readonly AUTHENTICATED_PATHS = [
    '/projects',
    '/project-configurations',
  ];

  constructor(private injector: Injector) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.needsAuth(request.url)) {
      return next.handle(request);
    }

    const token = this.readToken();
    const authorised = token
      ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : request;

    return next.handle(authorised).pipe(
      catchError((error: HttpErrorResponse) => {
        // A rejected token means the stored session is gone; drop it rather
        // than leaving the app in a half-signed-in state.
        if (error.status === 401 && token) {
          localStorage.removeItem(AuthInterceptor.USER_KEY);
          this.injector.get(Router).navigate(['/login']);
        }

        return throwError(() => error);
      })
    );
  }

  private needsAuth(url: string): boolean {
    if (!url.startsWith(environment.apiLink)) { return false; }

    const path = url.slice(environment.apiLink.length);
    return AuthInterceptor.AUTHENTICATED_PATHS.some(prefix => path.startsWith(prefix));
  }

  private readToken(): string | null {
    try {
      const raw = localStorage.getItem(AuthInterceptor.USER_KEY);
      if (!raw) { return null; }

      const parsed = JSON.parse(raw);
      return parsed?.token ?? null;
    } catch {
      return null;
    }
  }
}
